import React, { useEffect, useMemo, useState } from 'react';
import { adminService } from '../../services/adminService';
import { Profile } from '../context/AuthContext';
import { SocioEconomicData } from '../../types/domain';
import {
  getFamilyIncomeLabel,
  getFinancingTypeLabel,
  getParentEducationLabel
} from '../../lib/domain-data';
import {
  SocioEconomicFields,
  isSocioEconomicComplete,
  socioEconomicFromProfile
} from './SocioEconomicFields';

interface PatientSocioEconomicPanelProps {
  patient: Profile;
  /**
   * FR-03 makes these editable "by patient and admin". A doctor still sees
   * them — financing type is clinically relevant when discussing supplies —
   * but does not edit them, which is the one place this differs from the
   * glucose targets above it (B2 put those in a doctor's hands deliberately).
   */
  canEdit: boolean;
  /** Lets the dashboard keep its copy of the patient row in step after a save. */
  onSaved: (data: SocioEconomicData) => void;
}

/**
 * FR-03 socio-economic data on the doctor dashboard (SoW Bagian 2.3).
 *
 * Its own component rather than more JSX in Dashboard.tsx, which is already
 * long enough that the patient-detail card is hard to read.
 */
const PatientSocioEconomicPanel: React.FC<PatientSocioEconomicPanelProps> = ({
  patient,
  canEdit,
  onSaved
}) => {
  const stored = useMemo(() => socioEconomicFromProfile(patient), [patient]);
  const [draft, setDraft] = useState<SocioEconomicData>(stored);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Switching patient must reset the form, or the previous child's answers are
  // shown — and, worse, saved — against the new one.
  //
  // Keyed on the id, not on `stored`. The dashboard folds a successful save
  // back into its patient rows, which hands this component a new object in the
  // same render as setNotice() — depending on the object would clear the
  // "tersimpan" confirmation the instant it appeared.
  useEffect(() => {
    setDraft(socioEconomicFromProfile(patient));
    setError(null);
    setNotice(null);
  }, [patient.id]);

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await adminService.updatePatientSocioEconomic(patient.id, draft);
      onSaved(draft);
      setNotice('Data sosio-ekonomi tersimpan.');
    } catch (err: any) {
      setError(err?.message || 'Gagal menyimpan data sosio-ekonomi.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="patient-targets">
      <summary>
        Data sosio-ekonomi
        <span className="patient-targets-state">
          {isSocioEconomicComplete(stored) ? 'lengkap' : 'belum lengkap'}
        </span>
      </summary>

      <dl className="family-summary">
        <div className="family-summary-row">
          <dt>Pendidikan orang tua</dt>
          <dd>{getParentEducationLabel(stored.parentEducation) ?? 'Belum diisi'}</dd>
        </div>
        <div className="family-summary-row">
          <dt>Penghasilan keluarga</dt>
          <dd>{getFamilyIncomeLabel(stored.familyIncome) ?? 'Belum diisi'}</dd>
        </div>
        <div className="family-summary-row">
          <dt>Jenis pembiayaan</dt>
          <dd>{getFinancingTypeLabel(stored.financingType) ?? 'Belum diisi'}</dd>
        </div>
        <div className="family-summary-row">
          <dt>Nomor kontak orang tua</dt>
          <dd>{stored.parentContact ?? 'Belum diisi'}</dd>
        </div>
      </dl>

      {canEdit ? (
        <>
          <SocioEconomicFields
            value={draft}
            onChange={setDraft}
            disabled={busy}
            idPrefix={`patient-socio-${patient.id}`}
          />

          {error ? <div className="target-band-error">{error}</div> : null}
          {notice ? <div className="target-band-preview">{notice}</div> : null}

          <div className="target-actions">
            <button type="button" className="btn-primary" disabled={busy} onClick={handleSave}>
              {busy ? 'Menyimpan...' : 'Simpan data sosio-ekonomi'}
            </button>
          </div>
        </>
      ) : (
        <div className="subtitle" style={{ fontSize: 12 }}>
          Hanya admin dan pasien yang dapat mengubah data ini.
        </div>
      )}
    </details>
  );
};

export default PatientSocioEconomicPanel;
